import { useRouter } from "next/router";
import { useRolePermissionsRefresh } from "@Front/Stores/RolePermissionsStore";
import { useLoginState } from "./helpers/loginStateHook";
import { useCustomerLoginHandlers } from "@Front/Components/Layouts/Login/hooks/useCustomerLoginHandlers";
import { useLoginRouterScope } from "./useLoginRouterScope";
import { LoginStep } from "./loginSteps";
import { usePublicSiteDisplayName } from "@Front/Services/PublicSiteConfig/usePublicSiteDisplayName";
import { useLoginCustomerChoiceDerived } from "./useLoginCustomerChoiceDerived";

export { LoginStep };

export type LoginControllerReturn = {
	step: ReturnType<typeof useLoginState>["step"];
	/** Libellé verticale (`sites.label` / `display_name`), partagé entre les étapes login. */
	siteDisplayName: string | null;
	email: string;
	partialPhoneNumber: string;
	deliveryChannel: import("@Front/Api/Auth/Customer/Auth").DeliveryChannel;
	maskedEmail: string | undefined;
	validationErrors: import("class-validator").ValidationError[];
	loginCandidates: ReturnType<typeof useLoginState>["loginCandidates"];
	customerTargetChoiceCandidates: ReturnType<typeof useLoginState>["customerTargetChoiceCandidates"];
	customerOfficeChoiceEntries: import("@Front/Components/Layouts/Login/helpers/loginCustomerMultiCandidateRouting").OfficeChoiceEntry[];
	customerTargetChoiceSubtitle: string | undefined;
	onEmailFormSubmit: (e: React.FormEvent<HTMLFormElement> | null, values: Record<string, string>) => Promise<void>;
	onSmsCodeSubmit: (e: React.FormEvent<HTMLFormElement> | null, values: Record<string, string>) => Promise<void>;
	onNewPasswordSubmit: (e: React.FormEvent<HTMLFormElement> | null, values: Record<string, string>) => Promise<void>;
	onPasswordSubmit: (e: React.FormEvent<HTMLFormElement> | null, values: Record<string, string>) => Promise<void>;
	onPasswordForgotClicked: () => void;
	onSendAnotherCode: () => void;
	onChooseCustomerActor: () => Promise<void>;
	onChooseThirdPartyActor: () => void;
	onSelectCustomerOffice: (officeUid: string) => Promise<void>;
	onSelectCustomerLoginTarget: (row: import("@Front/Api/Public/emailAuthCandidatesApi").CustomerLoginCandidateDto) => Promise<void>;
	disambiguationBusy: boolean;
};

export function useLoginController(): LoginControllerReturn {
	const router = useRouter();
	const loginScope = useLoginRouterScope(router);
	const refresh = useRolePermissionsRefresh();
	const { siteDisplayName } = usePublicSiteDisplayName();
	const loginState = useLoginState();
	const { step, email, partialPhoneNumber, deliveryChannel, maskedEmail, validationErrors, loginCandidates, customerTargetChoiceCandidates } =
		loginState;

	const handlers = useCustomerLoginHandlers({
		router,
		loginScope,
		loginState,
		refresh,
	});

	const { customerOfficeChoiceEntries, customerTargetChoiceSubtitle } = useLoginCustomerChoiceDerived(
		loginScope,
		step,
		loginCandidates,
	);

	return {
		step,
		siteDisplayName,
		email,
		partialPhoneNumber,
		deliveryChannel,
		maskedEmail,
		validationErrors,
		loginCandidates,
		customerTargetChoiceCandidates,
		customerOfficeChoiceEntries,
		customerTargetChoiceSubtitle,
		...handlers,
	};
}
